import { CreateAttempt } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import {
  getCorrectSegmentsForDefaultGame,
  getCorrectSegmentsForHeartWords,
  getCorrectSegmentsForMorphMaster,
  getCorrectSegmentsForMysticMatch,
  getCorrectSegmentsForPhonemeForge,
  getCorrectSegmentsForSentenceBuilder,
  getCorrectSegmentsForWordRifts,
  getCorrectSegmentsForWordSums,
} from '@/common/utils/getCorrectSegments';
import { sanitize } from '@/common/utils/helpers';
import {
  segmentTypedAnswer,
  segmentTypedAnswerByWhitespace,
  validateSegmentedAnswer,
} from '@/common/utils/segmentTypedAnswer';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import {
  MINIGAME_CORRECT_COLOUR,
  MINIGAME_INCORRECT_COLOUR,
} from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { HorizontalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/HorizontalLayoutGroup';
import { fitToContent } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/LayoutGroupUtils';
import { didAttemptEarnSpeedBonus } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { HOVER_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import SingleCharacterText from '@/game/prefabs/StudentGame/SingleCharacterText';
import StudentGameAttemptUI from '@/game/prefabs/StudentGame/StudentGameAttemptUI/StudentGameAttemptUI';

export default class StudentGameAttemptUIController {
  constructor(private attemptUI: StudentGameAttemptUI) {
    this.initialize();
  }

  public onSpeedBonusIconHoverOn?: (icon: Phaser.GameObjects.Image) => void;
  public onSpeedBonusIconHoverOff?: () => void;

  studentAttemptTextLayoutGroup?: HorizontalLayoutGroup;
  studentAttemptTextLayoutGroup_m?: HorizontalLayoutGroup;
  correctAnswerTextLayoutGroup?: HorizontalLayoutGroup;
  correctAnswerTextLayoutGroup_m?: HorizontalLayoutGroup;

  getResponsiveElements(key: keyof StudentGameAttemptUI) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromContainer(
      this.attemptUI,
      key,
    );
  }

  initialize() {
    const { attemptUI } = this;

    // Set up speed bonus icon hover.
    this.getResponsiveElements('speedBonusIcon').forEach(
      (speedBonusIcon: Phaser.GameObjects.Image) => {
        speedBonusIcon.setInteractive({
          cursor: `${HOVER_CURSOR_PATH}, auto`,
        });
        speedBonusIcon.on('pointerover', () => {
          speedBonusIcon.setScale(2.2);
          PhaserAudioManager.playSFX(attemptUI.scene, 'hover-click');
          this.onSpeedBonusIconHoverOn?.(attemptUI.speedBonusIcon);
        });
        speedBonusIcon.on('pointerout', () => {
          speedBonusIcon.setScale(2);
          this.onSpeedBonusIconHoverOff?.();
        });
      },
    );

    // Text layout groups.
    const maxWidth = 220;
    this.studentAttemptTextLayoutGroup = new HorizontalLayoutGroup({
      parent: attemptUI.studentAttemptTextContainer,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 4,
      align: 'left',
      maxWidth,
    });
    this.correctAnswerTextLayoutGroup = new HorizontalLayoutGroup({
      parent: attemptUI.answerTextContainer,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 4,
      align: 'left',
      maxWidth,
    });

    // Mobile text layout groups.
    const maxWidth_m = 100;
    this.studentAttemptTextLayoutGroup_m = new HorizontalLayoutGroup({
      parent: attemptUI.studentAttemptTextContainer_m,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 2,
      align: 'left',
      maxWidth: maxWidth_m,
    });
    this.correctAnswerTextLayoutGroup_m = new HorizontalLayoutGroup({
      parent: attemptUI.answerTextContainer_m,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 2,
      align: 'left',
      maxWidth: maxWidth_m,
    });

    this.initializeResponsiveness();
  }

  setAttempt(attempt: CreateAttempt) {
    switch (attempt.activityName) {
      default:
        this.setAttemptForDefaultGame(attempt);
        break;
      // Temporarily disable the special sentence builder view.
      // Can bring it back later if desired.
      // case 'sentencebuilder':
      //   this.setAttemptForSentenceBuilder(attempt);
      //   break;
    }
  }

  setAttemptForDefaultGame(attempt: CreateAttempt) {
    const { attemptUI } = this;

    for (const letter of attempt.studentAnswer) {
      // Desktop letter.
      const letterText = new SingleCharacterText(attemptUI.scene, 0, 0);
      letterText.setCharacter(letter);
      letterText.overrideTextStyle({
        colour: 0x52333f,
        size: -32,
      });
      this.studentAttemptTextLayoutGroup?.addElement(letterText);

      // Mobile letter.
      const letterText_m = new SingleCharacterText(attemptUI.scene, 0, 0);
      letterText_m.setCharacter(letter);
      letterText_m.overrideTextStyle({
        colour: 0x52333f,
        size: -18,
      });
      this.studentAttemptTextLayoutGroup_m?.addElement(letterText_m);
    }
    for (const letter of attempt.correctAnswer) {
      // Desktop letter.
      const letterText = new SingleCharacterText(attemptUI.scene, 0, 0);
      letterText.setCharacter(letter);
      letterText.overrideTextStyle({
        colour: 0x52333f,
        size: -32,
      });
      this.correctAnswerTextLayoutGroup?.addElement(letterText);

      // Mobile letter.
      const letterText_m = new SingleCharacterText(attemptUI.scene, 0, 0);
      letterText_m.setCharacter(letter);
      letterText_m.overrideTextStyle({
        colour: 0x52333f,
        size: -18,
      });
      this.correctAnswerTextLayoutGroup_m?.addElement(letterText_m);
    }

    this.getResponsiveElements('studentAttemptTextContainer').forEach(
      (container: Phaser.GameObjects.Container) => {
        container.setVisible(true);
      },
    );

    this.getResponsiveElements('answerTextContainer').forEach(
      (answerTextContainer: Phaser.GameObjects.Container) => {
        answerTextContainer.setVisible(true);
      },
    );

    this.updateLayout();

    this.showAttemptValidation(attempt);
  }

  updateLayout() {
    const { attemptUI } = this;

    // Fit to content on Desktop.
    fitToContent(attemptUI.attemptBacking, attemptUI.resultsTextContainer, {
      top: 0,
      left: 0,
      bottom: 20,
    });

    // Fit to content on Mobile.
    fitToContent(attemptUI.attemptBacking_m, attemptUI.resultsTextContainer_m, {
      top: 0,
      left: 0,
      bottom: 10,
    });
  }

  setAttemptForSentenceBuilder(attempt: CreateAttempt) {
    const { attemptUI } = this;
    // Hide the answer text.
    attemptUI.answerTextContainer.setVisible(false);

    for (const letter of attempt.studentAnswer) {
      if (attemptUI.answerTextContainer.getBounds().width <= 500) {
        const letterText = new SingleCharacterText(attemptUI.scene, 0, 0);
        letterText.setCharacter(letter);
        letterText.overrideTextStyle({
          colour: 0x52333f,
          size: -22,
        });
        this.studentAttemptTextLayoutGroup?.addElement(letterText);

        const letterText_m = new SingleCharacterText(attemptUI.scene, 0, 0);
        letterText_m.setCharacter(letter);
        letterText_m.overrideTextStyle({
          colour: 0x52333f,
          size: -22,
        });
        this.studentAttemptTextLayoutGroup_m?.addElement(letterText_m);
      }
    }

    // Show the student's attempt in the place of the answer.
    attemptUI.studentAttemptTextContainer?.setPosition(
      attemptUI.answerTextContainer?.x,
      attemptUI.answerTextContainer?.y,
    );

    this.showAttemptValidation(attempt);
    this.updateLayout();
  }

  showAttemptValidation(attempt: CreateAttempt) {
    const attemptWasCorrect =
      sanitize(attempt.studentAnswer) === sanitize(attempt.correctAnswer);
    this.getResponsiveElements('attemptResultIcon').forEach(
      (attemptResultIcon: Phaser.GameObjects.Image) => {
        attemptResultIcon.setTexture(attemptWasCorrect ? 'tick' : 'cross');
      },
    );

    this.getResponsiveElements('speedBonusContainer').forEach(
      (speedBonusContainer: Phaser.GameObjects.Container) => {
        speedBonusContainer.setVisible(
          attemptWasCorrect && didAttemptEarnSpeedBonus(attempt),
        );
      },
    );

    const correctAnswerId = attempt.correctAnswerId;

    // Get correct segments
    const correctSegments = this.getCorrectSegmentsFromGame(
      attempt.activityName,
      correctAnswerId,
    );

    if (!correctSegments) return attempt.studentAnswer;

    let segments = attempt.cardsPlayed;
    if (attempt.activityName === 'sentencebuilder') {
      // Ensure consistency when comparing student answer to correct answer.
      segments = segments?.map((segment) => sanitize(segment)) ?? [];
    }

    if (attempt.control === ControllerType.KEYBOARD) {
      segments = this.getTypedSegmentsFromGame(
        attempt.activityName,
        attempt.studentAnswer,
        correctSegments,
      );
    }
    if (!segments) return;

    // Validate segments.
    const segmentsValid = validateSegmentedAnswer(segments, correctSegments);

    const studentAttemptText = this.studentAttemptTextLayoutGroup
      ?.getElementList()
      .filter((element) => element instanceof SingleCharacterText);
    const studentAttemptText_m = this.studentAttemptTextLayoutGroup_m
      ?.getElementList()
      .filter((element) => element instanceof SingleCharacterText);
    if (!studentAttemptText || !studentAttemptText_m) return;

    let letterIndex = 0;
    for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
      const segment = segments[segmentIndex];
      const segmentValid = segmentsValid[segmentIndex];
      const segmentLength = segment.length;

      for (let i = 0; i < segmentLength; i++) {
        // Style the desktop letter.
        const letterText = studentAttemptText[letterIndex];
        letterText.overrideTextStyle({
          colour: segmentValid
            ? MINIGAME_CORRECT_COLOUR
            : MINIGAME_INCORRECT_COLOUR,
          size: -28,
        });
        // Style the mobile letter.
        const letterText_m = studentAttemptText_m[letterIndex];
        letterText_m.overrideTextStyle({
          colour: segmentValid
            ? MINIGAME_CORRECT_COLOUR
            : MINIGAME_INCORRECT_COLOUR,
          size: -18,
        });
        letterIndex++;
      }
      if (attempt.activityName === 'sentencebuilder') {
        letterIndex++; // Space.
      }
    }
  }

  getCorrectSegmentsFromGame(game: string, itemId: string) {
    switch (game) {
      default:
        return getCorrectSegmentsForDefaultGame(itemId);
      case 'mysticmatch':
        return getCorrectSegmentsForMysticMatch(itemId);
      case 'morphmaster':
        return getCorrectSegmentsForMorphMaster(itemId);
      case 'phonemeforege':
        return getCorrectSegmentsForPhonemeForge(itemId);
      case 'wordsums':
        return getCorrectSegmentsForWordSums(itemId);
      case 'sentencebuilder':
        return getCorrectSegmentsForSentenceBuilder(itemId);
      case 'wordrifts':
        return getCorrectSegmentsForWordRifts(itemId);
      case 'trickywords':
        return getCorrectSegmentsForHeartWords(itemId);
    }
  }

  getTypedSegmentsFromGame(
    game: string,
    typedAnswer: string,
    correctSegments: string[],
  ) {
    switch (game) {
      default:
        return segmentTypedAnswer(typedAnswer, correctSegments);
      case 'sentencebuilder':
        return segmentTypedAnswerByWhitespace(typedAnswer);
    }
  }

  initializeResponsiveness() {
    const { attemptUI } = this;
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: attemptUI.scene,
      desktopView: attemptUI.desktopView,
      mobileView: attemptUI.mobileView,
    });
  }
}
